import { ClinicAuthError } from "@auth/clinicAuthClient";

// A type alias, not an interface: an interface lacks the index signature query-core's
// `Record<string, unknown>` constraint needs, and `meta` would silently fall back to untyped.
type MutationMeta = {
  /** Verb phrase completing "Couldn't …", e.g. `"verify the note"`. */
  action?: string;
  /** The call site renders its own failure UI, so the global notice stays quiet. */
  handled?: boolean;
};

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: MutationMeta;
  }
}

const FALLBACK_ACTION = "complete that action";

/** Brian often sends `detail` unpunctuated, so the terminator is added here. */
const withDetail = (
  detail: string | undefined,
  what: string,
  fallback: string,
): string => {
  const reason = detail?.trim();
  if (!reason) return fallback;
  const stop = /[.!?]$/.test(reason) ? "" : ".";
  return `${reason}${stop} Couldn’t ${what}.`;
};

/** Shared by the global notice and inline displays so a failure is never worded two ways. */
export const describeMutationError = (
  error: unknown,
  action?: string,
): string => {
  const what = action ?? FALLBACK_ACTION;
  const generic = `Couldn’t ${what}. Try again.`;

  if (!(error instanceof ClinicAuthError)) return generic;

  if (error.code === "network_error") return `No connection. Couldn’t ${what}.`;
  if (error.code === "invalid_credentials")
    return "Your session expired. Sign in again.";
  // The auth client says `rate_limited`; `request()` passes the bare 429.
  if (error.code === "rate_limited" || error.status === 429)
    return "Too many requests. Wait a moment, then try again.";

  switch (error.status) {
    case 403:
      return `You don’t have permission to ${what}.`;
    case 404:
      return `That’s no longer available. Couldn’t ${what}.`;
    case 409:
      return withDetail(
        error.detail,
        what,
        `Someone else got there first. Couldn’t ${what}.`,
      );
    case 422:
      return withDetail(error.detail, what, generic);
    default:
      if (error.status !== undefined && error.status >= 500)
        return `Something went wrong on our end. Couldn’t ${what}.`;
      return generic;
  }
};
